import { createRoot } from 'react-dom/client';
import { useOpenAIGlobal, useWidgetProps, useWidgetState } from 'react-openai-apps-sdk';
import { useSchedulingFlow } from './hooks/useSchedulingFlow';
import { generateTestData } from './utils/test-data';
import { SchedulingHeader } from './components/SchedulingHeader';
import { PageSelector } from './components/PageSelector';
import { BulkAssignment } from './components/BulkAssignment';
import { ReviewSummary } from './components/ReviewSummary';
import { SchedulingProgress } from './components/SchedulingProgress';
import { ResultsSummary } from './components/ResultsSummary';
import type { SchedulingData, SchedulingState } from './types';

function SchedulingWidget() {
  const theme = useOpenAIGlobal('theme') as string | undefined;
  const displayMode = useOpenAIGlobal('displayMode') as string | undefined;

  // Get data from MCP tool (posts + available pages)
  // In dev mode, use test data
  const rawData = useWidgetProps<SchedulingData>(
    import.meta.env.DEV ? generateTestData : undefined
  );

  const data: SchedulingData | null = rawData;

  // Persistent widget state
  const [widgetState, setWidgetState] = useWidgetState<SchedulingState>({
    currentStep: 'page_selection',
    selectedPageIds: [],
    postAssignments: {},
    scheduleResults: [],
  });

  // Main scheduling flow hook
  const {
    currentStep,
    selectedPages,
    postAssignments,
    scheduleResults,
    handleUpdatePageSelection,
    handleProceedToAssignment,
    handleAssignPosts,
    handleStartScheduling,
    handleBack,
    handleClose,
    schedulingProgress,
  } = useSchedulingFlow(data, widgetState || null, setWidgetState);

  // Handle moving to review step
  const handleMoveToReview = () => {
    setWidgetState((prev) => ({
      ...prev,
      currentStep: 'review',
    }));
  };

  // Render step content
  const renderStep = () => {
    if (!data) {
      return (
        <div className="text-center py-8 text-muted-foreground">
          Loading scheduling data...
        </div>
      );
    }

    switch (currentStep) {
      case 'page_selection':
        return (
          <PageSelector
            availablePages={data.availablePages}
            selectedPageIds={widgetState?.selectedPageIds || []}
            onUpdateSelection={handleUpdatePageSelection}
            onNext={handleProceedToAssignment}
          />
        );

      case 'bulk_assignment':
        return (
          <BulkAssignment
            posts={data.posts}
            selectedPages={selectedPages}
            postAssignments={postAssignments}
            onAssignPosts={handleAssignPosts}
            onBack={handleBack}
            onNext={handleMoveToReview}
          />
        );

      case 'review':
        return (
          <ReviewSummary
            posts={data.posts}
            selectedPages={selectedPages}
            postAssignments={postAssignments}
            onBack={handleBack}
            onConfirm={handleStartScheduling}
          />
        );

      case 'scheduling':
        return (
          <SchedulingProgress
            posts={data.posts}
            postAssignments={postAssignments}
            scheduleResults={scheduleResults}
            progress={schedulingProgress}
          />
        );

      case 'results':
        return (
          <ResultsSummary
            posts={data.posts}
            selectedPages={selectedPages}
            postAssignments={postAssignments}
            scheduleResults={scheduleResults}
            onClose={handleClose}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className={`scheduling-widget theme-${theme}`} data-display-mode={displayMode}>
      <div className="max-w-4xl mx-auto p-6">
        <SchedulingHeader currentStep={currentStep} />
        {renderStep()}
      </div>
    </div>
  );
}

const root = document.getElementById('scheduling-root');
if (root) {
  createRoot(root).render(<SchedulingWidget />);
}
